export interface TellentPosition {
  guid: string;
  members: TellentPositionMember[];
  membersCount: number;
  name: string;
  descriptionHtml?: string | null;
  descriptionJson?: unknown | null;
  deletedAt: string | null;
}

export interface TellentPositionMember {
  assignedAt: string;
  avatarThumbUrl: string | null;
  email: string;
  firstName: string;
  guid: string;
  hasWorkEmail: boolean;
  lastName: string;
  login: string | null;
}

// The subset of a position that identifies it and names it. Consumers that only label a position
// take this, so callers holding no more than a guid and a name — the Manage side of the
// `tellent-client` boundary — can satisfy them without inventing the rest of a `TellentPosition`.
export type TellentPositionRef = Pick<TellentPosition, 'guid' | 'name'>;

export type TellentPositionMemberRef = Pick<
  TellentPositionMember,
  'guid' | 'firstName' | 'lastName' | 'email' | 'avatarThumbUrl'
>;

export interface TellentPositionResponse {
  data: TellentPosition;
}

export interface PositionDetailsDialogData {
  positionGuid: string;
}

export interface UpdatePositionPayload {
  name?: string;
  descriptionJson?: unknown | null;
}

export interface GeneratePositionDescriptionPayload {
  positionName: string;
}

export interface GeneratePositionDescriptionResponse {
  descriptionHtml: string;
}

export interface DeletePositionDialogData {
  position: TellentPosition;
}

export interface DeletePositionDialogResult {
  success: boolean;
}

export interface RenamePositionDialogData {
  positionGuid: string;
  positionName: string;
}

export interface RenamePositionDialogResult {
  success: boolean;
}

export interface PositionMemberConflict {
  member: TellentPositionMember;
  currentPosition: TellentPosition;
}

export interface ReassignMemberWarningDialogData {
  targetPositionName: string;
  members: PositionMemberConflict[];
}

export interface ReassignMemberWarningDialogResult {
  confirmed: boolean;
}

export interface ConfirmPositionChangeMember {
  guid: string;
  fullName: string;
  avatarThumbUrl: string | null;
  currentPosition: TellentPositionRef | null;
}

export interface ConfirmPositionChangeDialogData {
  targetPositionName: string;
  members: ConfirmPositionChangeMember[];
}

export interface ConfirmPositionChangeDialogResult {
  confirmed: boolean;
  addToHistory: boolean;
  effectiveDate: string;
}

// What applying a confirmed change amounted to, for callers that only drive the dialog and report
// the result — the Manage integration, which continues its own save on success.
export interface ConfirmPositionChangeOutcome {
  status: 'cancelled' | 'success' | 'failed';
  // The change was added to history with a future effective date, so the backend wrote a
  // `scheduled` assignment and left the current one alone.
  isScheduled: boolean;
  errorMessage?: string;
}

export interface AssignMembersOptions {
  startDate: string;
}

export interface UnassignMemberFromPositionDialogData {
  positionData: TellentPositionRef;
  member: TellentPositionMemberRef;
}

export interface UnassignMemberFromPositionDialogResult {
  success: boolean;
}

export enum PositionAssignmentStatus {
  Scheduled = 'scheduled',
  Current = 'current',
  Past = 'past',
}

export interface PositionAssignment {
  id: number;
  positionGuid: string;
  positionName: string;
  memberGuid: string;
  startDate: string;
  status: PositionAssignmentStatus;
}

// A null `id` creates a new assignment; anything else rewrites that stored row. `status` is stated
// rather than inferred — the backend no longer derives it from `startDate`.
export interface PositionHistoryEntryPayload {
  id: number | null;
  positionGuid: string;
  startDate: string;
  status: PositionAssignmentStatus;
}

// A member holds at most one scheduled assignment, and the update endpoint replaces the whole
// history — a row the dialog does not render is a row the next save deletes.
export interface MemberPositionHistory {
  scheduled: PositionAssignment[];
  current: PositionAssignment | null;
  previous: PositionAssignment[];
}

export interface PositionHistoryDialogData {
  memberGuid: string;
  canEdit?: boolean;
  // Reading `/members/:guid` needs `manage_users`, which the members the dialog is now open to do
  // not have. Callers that already know the member pass them in so the header still has a name.
  memberName?: string;
  memberAvatarUrl?: string | null;
}

export enum PositionDetailsTab {
  Employees = 'employees',
  Description = 'description',
  Skills = 'skills',
}

export enum AddSkillsDropdownAction {
  CreateNewSkill = 'create-new-skill',
  ManageSkillMappings = 'manage-skill-mappings',
  ImportFromFile = 'import-from-file',
  GenerateSuggested = 'generate-suggested',
}
